import { IconButton } from '@legacy/components/mui-components';
import { InfoOutlined } from '@mui/icons-material';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translationAnyText } from '@legacy/utils/translation';
import ReportingWidgetInfoModal from '@legacy/screens/Home/components/ReportingWidgetInfoModal';
import { Typography, Stack, Card, CardHeader, Grid, Box } from '@mui/material';
import WidgetWithoutChartShimmer from '../WidgetWithoutChartShimmer';
import WidgetError from '../WidgetError';
import { IWidgetWithoutChart } from '../../HomeType';
import { ValueText } from './components/ValueText';
import styles from './WidgetWithoutChart.module.scss';
import { useIsMobile } from '@/hooks/useIsMobile';

export default ({
  queryKey,
  informationList,
  relId,
  isLoading = false,
  isError = false,
  toggleContent,
}: IWidgetWithoutChart) => {
  const { t } = useTranslation('frontpage');
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  if (isLoading) {
    return <WidgetWithoutChartShimmer />;
  }
  if (isError) {
    return <WidgetError queryKey={queryKey} />;
  }

  return (
    <Card
      className={`Card-${relId}`}
      variant="outlined"
      sx={{ height: '100%' }}
    >
      <CardHeader
        title={
          <Grid container>
            <Grid size={{ sm: 12, md: 6 }}>
              <Typography
                className={styles.widgetTitle}
                component="h2"
                variant="h4"
                data-automation-id={`WidgetHeaderHeading${relId}`}
              >
                {t(`WidgetTitle${relId}`)}
              </Typography>
            </Grid>
            <Grid size={{ sm: 12, md: 6 }}>
              <Box
                display="flex"
                justifyContent={isMobile ? 'flex-start' : 'flex-end'}
              >
                {toggleContent && toggleContent}
              </Box>
            </Grid>
          </Grid>
        }
        action={
          <Fragment key={`InfoIconButton${relId}`}>
            <IconButton
              title={t('InfoIconTooltipTitle')}
              size="small"
              aria-label={t('InfoIconTooltipTitle')}
              data-automation-id={`InfoIconButton${relId}`}
              onClick={() => setIsOpen(true)}
            >
              <InfoOutlined />
            </IconButton>
            <ReportingWidgetInfoModal
              id={relId}
              title={t(`WidgetTitle${relId}`)}
              infoMessage={t(`WidgetTipsContent${relId}`)}
              openState={isOpen}
              setOpenState={setIsOpen}
            />
          </Fragment>
        }
      />
      <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
        <Stack component="dl" spacing={1}>
          {informationList.map(
            ({
              value,
              trending = null,
              trendingDirection,
              key,
              abbrTitle,
              abbrText,
            }) => (
              <div className={styles.defWrapper} key={`stack${key}`}>
                {translationAnyText(t, `Widget${relId}${key}Name`) !==
                  `Widget${relId}${key}Name` && (
                  <Typography
                    component="dt"
                    data-automation-id={`Widget${relId}${key}Name`}
                  >
                    {translationAnyText(t, `Widget${relId}${key}Name`)}
                  </Typography>
                )}
                <ValueText
                  value={value}
                  trending={trending}
                  trendingDirection={trendingDirection}
                  key={key}
                  data-automation-id={`Widget${relId}${key}Value`}
                  translationKey={`Widget${relId}${key}Value`}
                  abbrText={abbrText}
                  abbrTitle={abbrTitle}
                />
              </div>
            ),
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
